<template>
  <div class="h-screen bg-base-200 flex flex-col overflow-hidden" :data-theme="currentTheme">
    <Analytics />
    <!-- Top Nav — glassmorphism, no border (structure via bg-base-100/70) -->
    <div class="navbar shrink-0 z-50 bg-base-100/70 backdrop-blur-xl px-4 lg:px-8 border-b border-base-content/5">
      <div class="navbar-start">
        <router-link to="/" class="btn btn-ghost text-xl font-bold gap-2 px-2">
          Stock Jockey
        </router-link>
      </div>

      <div class="navbar-center">
        <router-link
          to="/competition"
          class="btn btn-ghost btn-sm"
          :class="{ 'btn-active': $route.path === '/competition' }"
        >
          Live Demo
        </router-link>
      </div>

      <div class="navbar-end gap-1 sm:gap-2">
        <button
          type="button"
          class="btn btn-ghost btn-sm gap-1.5 normal-case font-normal text-warning hover:bg-warning/10 border border-warning/30 rounded-lg"
          title="All platform data is fictional — tap to read disclaimer"
          aria-label="Fictional data notice — opens disclaimer"
          @click="openFictionalDisclaimer"
        >
          <i class="mdi mdi-alert-decagram-outline text-lg shrink-0" aria-hidden="true" />
          <span class="hidden sm:inline">
            Fictional demo data
          </span>
        </button>
        <button @click="toggleTheme" class="btn btn-ghost btn-circle">
          <i v-if="currentTheme === 'sj-dark'" class="mdi mdi-weather-sunny text-lg"></i>
          <i v-else class="mdi mdi-weather-night text-lg"></i>
        </button>
      </div>
    </div>

    <!-- Main Content -->
    <main class="flex-1 overflow-y-auto">
      <div v-if="$route.path === '/'" class="p-0 h-full">
        <router-view />
      </div>
      <div v-else class="p-4 lg:p-8">
        <router-view />
      </div>
    </main>

    <footer class="shrink-0 border-t border-base-content/10 bg-base-100/80 px-4 py-3 backdrop-blur-lg lg:px-8">
      <div class="flex items-center justify-center gap-2">
        <span class="text-sm opacity-70">
          <span class="font-bold">Stock Jockey</span> is a project by <a href="https://github.com/J-TUREK" target="_blank" rel="noopener noreferrer" class="link link-hover">J-TUREK</a>
        </span>
        <a
          :href="githubUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-ghost btn-sm gap-2"
          aria-label="Open GitHub profile"
          title="GitHub"
        >
          <i class="mdi mdi-github text-lg"></i>
          <span>GitHub</span>
        </a>
      </div>
    </footer>

    <FictionalDataDisclaimerModal
      v-model="fictionalDisclaimerOpen"
      @acknowledge="acknowledgeFictionalDisclaimer"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Analytics } from '@vercel/analytics/vue'
import { useAuthStore } from './stores/auth'
import FictionalDataDisclaimerModal from './components/FictionalDataDisclaimerModal.vue'
import { useFictionalDataDisclaimer } from './composables/useFictionalDataDisclaimer.js'

const authStore = useAuthStore()
const { modalOpen: fictionalDisclaimerOpen, acknowledge: acknowledgeFictionalDisclaimer, openModal: openFictionalDisclaimer } =
  useFictionalDataDisclaimer()
const currentTheme = ref('sj-dark')
const githubUrl = 'https://github.com/J-TUREK'

const toggleTheme = () => {
  setTheme(currentTheme.value === 'sj-dark' ? 'sj-light' : 'sj-dark')
}

const setTheme = (theme) => {
  currentTheme.value = theme
  localStorage.setItem('theme', theme)
  document.documentElement.setAttribute('data-theme', theme)
}

onMounted(async () => {
  const saved = localStorage.getItem('theme') || 'sj-dark'
  setTheme(saved)
  await authStore.initializeAuth()
})
</script>
